import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ApiError } from '../../api/errors';
import { stockApi } from '../../api/stock-api';
import type { StockItem } from '../../domain/fulfillment';
import { preparePhoto } from '../../lib/stock-photo';
import { stockErrorMessage } from './error-message';

/**
 * The stock item's photo, saved as soon as it's chosen or removed
 * (tidaltech/koi#8079). onChanged gets the updated item, whose version the
 * editor needs for its own save.
 */
export function StockItemPhotoField({
  item,
  onChanged,
}: {
  item: StockItem;
  onChanged: (updated: StockItem) => void;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  function failed(failure: unknown, fallback: string): void {
    // preparePhoto's own errors are already meant for people.
    setError(
      failure instanceof Error && !(failure instanceof ApiError)
        ? failure.message
        : stockErrorMessage(failure, fallback),
    );
  }
  async function saved(updated: StockItem): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['stock'] });
    await queryClient.invalidateQueries({ queryKey: ['stock-items'] });
    onChanged(updated);
  }

  const upload = useMutation({
    mutationFn: async (file: File) => stockApi.setStockItemImage(item.id, await preparePhoto(file)),
    onSuccess: saved,
    onError: (failure: unknown) => failed(failure, 'The photo could not be saved.'),
  });
  const remove = useMutation({
    mutationFn: () => stockApi.deleteStockItemImage(item.id),
    onSuccess: saved,
    onError: (failure: unknown) => failed(failure, 'The photo could not be removed.'),
  });

  const url = stockApi.stockItemImageUrl(item);
  const busy = upload.isPending || remove.isPending;
  return (
    <div className="stock-photo-field" role="group" aria-label="Photo">
      <span className="stock-photo-label">Photo</span>
      {url ? (
        <img src={url} alt={`Photo of ${item.displayName}`} className="stock-photo-preview" />
      ) : (
        <p className="field-hint">No photo yet.</p>
      )}
      <div className="button-group">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}>
          {upload.isPending ? 'Saving photo…' : url ? 'Replace photo' : 'Add photo'}
        </button>
        {url ? (
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setError(null);
              remove.mutate();
            }}
            disabled={busy}
          >
            Remove photo
          </button>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/*"
        className="visually-hidden"
        aria-label="Choose a photo"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          setError(null);
          upload.mutate(file);
        }}
      />
      {error ? (
        <p role="alert" className="form-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
